/**
 * Advisory Thunks
 *
 * The four listener-lifecycle thunks connecting the collision-proof
 * `advisoryListenerManager` to the Redux store.
 *
 * ## Plain thunks, not `createAsyncThunk`, not RTK listener middleware
 *
 * Each exported thunk is a plain function returning a
 * `(dispatch) => void | Promise<void>` closure — dispatchable against a real
 * store with zero components mounted, which makes the listener lifecycle
 * testable without any UI. `createAsyncThunk` would attach a
 * request-lifecycle action-type triple these thunks have no use for, and RTK
 * listener middleware would couple lifecycle to action shapes not yet
 * defined elsewhere in this feature.
 *
 * ## Dispatch typing: `Dispatch<AnyAction>`, never `AppDispatch`
 *
 * Every thunk types its `dispatch` parameter as `Dispatch<AnyAction>`,
 * imported type-only from `@reduxjs/toolkit` (whose `dist/index.d.ts` line 1
 * is `export * from 'redux';`, so both types resolve from this package —
 * `redux` itself is NOT a direct dependency of gchat and must not be
 * imported). These thunks only ever dispatch plain slice actions, so the
 * narrower type is honest, and — more importantly — it keeps this module
 * free of any import of `store.ts` (which imports `rootReducer.ts`, which
 * imports this feature's own barrel). A test can therefore dispatch these
 * thunks against a locally configured store with no cast. The real
 * application store's `AppDispatch` is assignable to `Dispatch<AnyAction>`,
 * so nothing changes for a real caller.
 *
 * ## Acyclic import: never a top-level `import ... from './advisorySlice'`
 *
 * The two `start*` thunks are `async` and begin their body with a single
 * dynamic import of `./advisorySlice`, destructuring every action they need
 * once in closure so the listener callbacks below dispatch synchronously
 * rather than each awaiting its own dynamic import. This mirrors
 * `chatThunks.ts:106`.
 *
 * The two `stop*` thunks are synchronous and dispatch nothing — they only
 * call the manager's `cleanup*` functions. This start/stop asymmetry is not
 * about registration/cleanup guards — neither direction here carries one.
 * The asymmetry here is purely "start touches slice state via dynamic
 * import, stop does not need to."
 *
 * ## No `has(key)` guard, on either side
 *
 * `advisoryListeners.ts`'s `register()` (lines 141-148) is already
 * remove-then-set, and `remove()` (lines 151-157) is already a no-op on an
 * unknown key — both directions are already safe at the manager level. A
 * registration guard here paired with an unconditional cleanup shipped as a
 * real defect on an earlier branch of this codebase; the fix there was to
 * delete the guard, not add a matching one. This module therefore never
 * calls `getEntry`, `getChannelState` or `getActiveListenerCount` to decide
 * whether to register or clean up. (Reading `getEntry` inside
 * `onChannelState` below, to fetch a recorded error CODE, is a different
 * thing and is required — see the next section.)
 *
 * ## Error-code-only boundary
 *
 * `onChannelState` only dispatches an error action when the emitted
 * `ChannelState` is `'denied'` or `'failed'` — `'loading'`, `'ready'` and
 * `'empty'` are ignored because the data path (`onData`) already carries
 * those, and `AdvisoryState` has no top-level loading flag by construction.
 * The error action needs an error CODE, not a `ChannelState` string, so the
 * code is read back off the manager via
 * `advisoryListenerManager.getEntry(key)?.status.errorCode`, falling back to
 * `UNKNOWN_ADVISORY_ERROR_CODE` when it is null or the entry is gone.
 * `mapAdvisoryErrorCode` maps error CODES — passing it the literal string
 * `'denied'` would silently fall through to the `unknown` entry, losing the
 * `permission-denied` mapping this feature's error-tone contract depends on.
 */

import type { AnyAction, Dispatch } from '@reduxjs/toolkit';
import { UNKNOWN_ADVISORY_ERROR_CODE } from './advisoryErrorCodes';
import {
  advisoryListenerManager,
  buildListenerKey,
  cleanupGoalListener,
  cleanupEngagementsListener,
  cleanupKeyQuestionsListener,
  setupEngagementsListener,
  setupGoalListener,
  setupKeyQuestionsListener,
} from './advisoryListeners';
import type { ChannelState } from './advisoryTypes';

/** Reads the manager's recorded error code for `key`, or the unknown fallback. */
function readErrorCode(key: string): string {
  return advisoryListenerManager.getEntry(key)?.status.errorCode ?? UNKNOWN_ADVISORY_ERROR_CODE;
}

/**
 * Starts the engagements-list listener for `userId`. Unconditional — no
 * guard around `setupEngagementsListener`.
 */
export function startAdvisoryListListener(userId: string) {
  return async (dispatch: Dispatch<AnyAction>): Promise<void> => {
    const { setEngagements, setEngagementsError } = await import('./advisorySlice');
    const key = buildListenerKey({ kind: 'engagements', userId });

    setupEngagementsListener({
      userId,
      onData: data => dispatch(setEngagements(data)),
      onChannelState: (state: ChannelState) => {
        if (state === 'denied' || state === 'failed') {
          dispatch(setEngagementsError(readErrorCode(key)));
        }
      },
    });
  };
}

/**
 * Stops the engagements-list listener for `userId`. Unconditional — no
 * guard around `cleanupEngagementsListener`; it is already a no-op on an
 * unregistered key.
 */
export function stopAdvisoryListListener(userId: string) {
  return (_dispatch: Dispatch<AnyAction>): void => {
    cleanupEngagementsListener(userId);
  };
}

/**
 * Starts the two project-scoped listeners (`goal`, `keyQuestions`) for
 * `projectId`.
 *
 * Cross-project cleanup is NOT automatic. `buildListenerKey({kind: 'goal',
 * userId, projectId: 'A'})` and the same call with `projectId: 'B'` are two
 * structurally different keys by design (see `advisoryListeners.ts`'s
 * module header) — the manager's remove-then-set inside `register()` only
 * protects re-registration under the SAME key, so navigating
 * engagement-to-engagement without a full unmount would leave the previous
 * project's two listeners live unless this thunk tears them down
 * explicitly. `previousProjectId` is caller-supplied information: if the
 * caller does not pass it, no cross-project teardown happens (this is the
 * documented contract, not an emergent property of the manager).
 */
export function startEngagementListeners(params: {
  userId: string;
  projectId: string;
  previousProjectId?: string;
}) {
  const { userId, projectId, previousProjectId } = params;
  return async (dispatch: Dispatch<AnyAction>): Promise<void> => {
    const { setGoalDoc, mergeKeyQuestionsSnapshot, setChannelError, removeEngagement } =
      await import('./advisorySlice');

    // Explicit teardown of the PREVIOUS project's two listeners before
    // registering the new project's. Nothing in the manager does this
    // automatically across a projectId change.
    if (previousProjectId && previousProjectId !== projectId) {
      cleanupGoalListener(userId, previousProjectId);
      cleanupKeyQuestionsListener(userId, previousProjectId);
    }

    const goalKey = buildListenerKey({ kind: 'goal', userId, projectId });
    const keyQuestionsKey = buildListenerKey({ kind: 'keyQuestions', userId, projectId });

    // Unconditional — no guard around either of the two setup* calls.
    setupGoalListener({
      userId,
      projectId,
      onData: doc => dispatch(setGoalDoc({ projectId, doc })),
      onDeleted: () => dispatch(removeEngagement(projectId)),
      onChannelState: (state: ChannelState) => {
        if (state === 'denied' || state === 'failed') {
          dispatch(setChannelError({ projectId, kind: 'goal', errorCode: readErrorCode(goalKey) }));
        }
      },
    });

    setupKeyQuestionsListener({
      userId,
      projectId,
      // Both this `onSnapshot` branch AND the local-mode poll branch
      // inside `setupKeyQuestionsListener` deliver "the full current
      // array", so both route through the guarded merge — never a
      // full-list replace.
      onData: keyQuestions =>
        dispatch(mergeKeyQuestionsSnapshot({ projectId, fetched: keyQuestions, now: Date.now() })),
      onChannelState: (state: ChannelState) => {
        if (state === 'denied' || state === 'failed') {
          dispatch(
            setChannelError({
              projectId,
              kind: 'keyQuestions',
              errorCode: readErrorCode(keyQuestionsKey),
            })
          );
        }
      },
    });
  };
}

/**
 * Key-question removal goes through `useSupervisorTurn.ts`'s `send()` with
 * `{projectId, removeQuestions: [id]}` — never a local optimistic splice.
 * The rendered set comes straight from that response's own `questions`
 * payload. `AdvisoryEngagementPage.tsx`'s `handleRemoveKeyQuestion` is the
 * call site.
 *
 * The generic recently-mutated guard machinery
 * (`markRecentlyMutated`/`clearRecentlyMutated`/`upsertKeyQuestion`/
 * `removeKeyQuestion` on `advisorySlice.ts`, `recentlyMutated` on
 * `advisoryInitialState.ts`, `GUARD_MAX_PENDING_MS`/`GUARD_TTL_MS` on
 * `advisoryPolling.ts`) has no current caller in this file, but is not dead
 * code: it is generic infrastructure, exercised independently by
 * `advisoryPolling.test.ts`'s own guard-both-directions suite (which also
 * covers a hypothetical in-flight ADD, not just a removal), and available
 * to any future optimistic-write call site.
 */

/**
 * Stops the two project-scoped listeners (`goal`, `keyQuestions`) for
 * `projectId`. Unconditional — each `cleanup*` call is already a no-op on
 * an unregistered key.
 */
export function stopEngagementListeners(params: { userId: string; projectId: string }) {
  const { userId, projectId } = params;
  return (_dispatch: Dispatch<AnyAction>): void => {
    cleanupGoalListener(userId, projectId);
    cleanupKeyQuestionsListener(userId, projectId);
  };
}
